import { Box, Container, Typography, Link as MuiLink, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'what-are-cookies', title: 'What are cookies?' },
  { id: 'how-we-use-cookies', title: 'How we use cookies in Galldris Core' },
  { id: 'strictly-necessary-cookies', title: 'Strictly necessary cookies' },
  { id: 'optional-cookies', title: 'Optional or third-party cookies' },
  { id: 'local-storage', title: 'Local storage and similar technologies' },
  { id: 'control-cookies', title: 'How to control or delete cookies' },
  { id: 'more-information', title: 'More information' },
];

export default function CookiePolicy() {
  return (
    <Box sx={{ py: 4, px: 2, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Cookie Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Last updated: March 2026
        </Typography>

        <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Contents
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            {SECTIONS.map(({ id, title }) => (
              <li key={id}>
                <MuiLink href={`#${id}`} underline="hover" color="primary" sx={{ fontSize: '0.8125rem' }}>
                  {title}
                </MuiLink>
              </li>
            ))}
          </Box>
        </Paper>

        <Typography id="what-are-cookies" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          1. What are cookies?
        </Typography>
        <Typography variant="body2" paragraph>
          Cookies are small text files stored on your device when you visit a website. They are widely used to make sites work properly, remember your preferences, or understand how the site is used.
        </Typography>

        <Typography id="how-we-use-cookies" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          2. How we use cookies in Galldris Core
        </Typography>
        <Typography variant="body2" paragraph>
          Galldris Core uses cookies and similar technologies only where necessary to provide and secure the service.
        </Typography>

        <Typography id="strictly-necessary-cookies" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          3. Strictly necessary cookies
        </Typography>
        <Typography variant="body2" paragraph>
          These cookies are essential for the application to function. They cannot be switched off. We use them for:
        </Typography>
        <Typography component="ul" variant="body2" sx={{ pl: 2, mb: 2 }}>
          <li><strong>Authentication and session:</strong> Keeping you logged in and remembering your session (e.g. refresh token or session identifier stored in a secure, HttpOnly cookie where applicable). Without these, you would have to log in again on every page or after closing the browser.</li>
          <li><strong>Security:</strong> Helping protect against cross-site request forgery (CSRF) and supporting secure access to the API.</li>
        </Typography>
        <Typography variant="body2" paragraph>
          We do not use strictly necessary cookies for tracking or advertising. They are not used to identify you outside this application.
        </Typography>

        <Typography id="optional-cookies" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          4. Optional or third-party cookies
        </Typography>
        <Typography variant="body2" paragraph>
          At present, Galldris Core does not set optional analytics, marketing, or third-party advertising cookies. If we introduce such cookies in the future, we will update this policy and, where required by law, ask for your consent before activating them. You will be able to accept or decline non-essential cookies.
        </Typography>

        <Typography id="local-storage" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          5. Local storage and similar technologies
        </Typography>
        <Typography variant="body2" paragraph>
          The application may use browser local storage or session storage (e.g. to cache preferences or support offline features). These do not send data to third parties by default and are used only to improve your use of the application on your device.
        </Typography>

        <Typography id="control-cookies" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          6. How to control or delete cookies
        </Typography>
        <Typography variant="body2" paragraph>
          You can set your browser to block or delete cookies. Note that blocking strictly necessary cookies will prevent you from using Galldris Core as intended (e.g. you may not be able to stay logged in). For more information, see your browser’s help or settings (e.g. &quot;Privacy and security&quot; or &quot;Cookies&quot;).
        </Typography>

        <Typography id="more-information" variant="h6" component="h2" sx={{ mt: 3, mb: 1, scrollMarginTop: 16 }}>
          7. More information
        </Typography>
        <Typography variant="body2" paragraph>
          For details on how we process your personal data, including data collected via cookies, see our{' '}
          <MuiLink component={Link} to="/privacy-policy" underline="hover">
            Privacy Notice
          </MuiLink>.
        </Typography>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <MuiLink component={Link} to="/login" underline="hover" sx={{ mr: 2 }}>
            Back to login
          </MuiLink>
          <MuiLink component={Link} to="/privacy-policy" underline="hover">
            Privacy Notice
          </MuiLink>
        </Box>
      </Container>
    </Box>
  );
}
